import { rtkPrepareHeadersCallback } from '@Api/Api'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react'
import { handleUnauthorized } from '@Features/Base/BaseApi/handleUnauthorized'
import { UserProgress, UserProgressPayload } from './Progress.types'

const progressBaseQuery = fetchBaseQuery({
	baseUrl: `${process.env.REACT_APP_API_BASE_URL}/student/progress/`,
	prepareHeaders: rtkPrepareHeadersCallback,
	// The SECOND RTK root. It shares `prepareHeaders` with BaseApi but has its
	// own `fetchBaseQuery`, so credentials have to be set here too — and the
	// endpoint below is declared `builder.query` while issuing a POST, so it
	// needs the CSRF token like any mutation.
	credentials: 'include',
})

/**
 * This root had NO error handling whatsoever, so a 401 here did nothing at all
 * — the request failed silently and the app carried on as though signed in.
 * Six lines to close a gap that only exists because there are two roots.
 */
const progressBaseQueryWithInterceptor: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
	args,
	api,
	extraOptions
) => {
	const result = await progressBaseQuery(args, api, extraOptions)
	if (result.error && result.error.status === 401) {
		handleUnauthorized(api.dispatch, result.error)
	}
	return result
}

export const ProgressAPI = createApi({
	reducerPath: 'ProgressAPI',
	baseQuery: progressBaseQueryWithInterceptor,
	endpoints: builder => ({
		getAllClassroomsProgress: builder.query<ApiResponse<UserProgress>, UserProgressPayload>({
			query: (info: UserProgressPayload) => {
				return {
					url: '',
					method: 'POST',
					body: info,
				}
			},
		}),
	}),
})

export const { useGetAllClassroomsProgressQuery } = ProgressAPI
